// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// WB-006 — late-join & reconnect state sync. A participant who opens the board after
// drawing has started — or who reconnects — must see the current state, not a blank
// canvas. The exchange rides whichever transport is active (data channel for 1:1/mesh,
// the m.whiteboard frame for SFU) as two control ops from WB-002:
//
//   snapshot_request — "I just joined, who has the board?"
//   snapshot         — the current vector op-set, sent by the ELECTED responder only.
//
// This coordinator is transport- and engine-agnostic: the Phase-2/3 host supplies the
// callbacks (send an op, read the engine's op-set, apply ops into the engine, list the
// connected peers). Keeping it standalone lets the election + merge logic be unit-tested
// without a live call.

import { electSnapshotResponder, OpDedup } from "./transport";
import type { Op, WireOp, SnapshotOp, SnapshotRequestOp } from "./types";

export interface LateJoinDeps {
  /** This client's user id. */
  myUserID: string;
  /** Send an op over the active transport (broadcast to all participants). */
  send: (op: WireOp) => void;
  /** The engine's current visible op-set (what a snapshot should carry). */
  snapshotOps: () => Op[];
  /** Merge a received snapshot's ops into the engine (idempotent — engine de-dupes). */
  applySnapshot: (ops: Op[]) => void;
  /** The currently-connected peer user ids (for responder election). */
  peers: () => string[];
  /** Unique op-id generator. */
  newId: () => string;
  /** Clock. */
  now: () => number;
  /** Optional board id stamped on the control ops (scopes Mode A vs Mode B). */
  boardId?: string;
}

export class LateJoinSync {
  // De-dupe request + snapshot ids so a re-delivered control op doesn't make us answer
  // twice or re-apply a snapshot.
  private readonly seen = new OpDedup();

  constructor(private readonly deps: LateJoinDeps) {}

  /** Ask the other participants for the current board (call on open / late join). */
  request(): void {
    const op: SnapshotRequestOp = {
      op: "snapshot_request",
      id: this.deps.newId(),
      author: this.deps.myUserID,
      ts: this.deps.now(),
      ...(this.deps.boardId ? { board_id: this.deps.boardId } : {}),
    };
    this.seen.first(op.id); // don't answer our own request if it echoes back
    this.deps.send(op);
  }

  /**
   * Handle an inbound control op. `from` is the receiver-stamped author (the requester
   * for a request; the responder for a snapshot). Returns true if it was a late-join
   * control op (so the host needn't route it to the engine), false otherwise.
   */
  onControl(op: WireOp, from: string): boolean {
    if (op.op === "snapshot_request") {
      if (!this.seen.first(op.id)) return true; // already handled
      // Only the elected responder answers — avoids N duplicate snapshots.
      if (electSnapshotResponder(this.deps.myUserID, this.deps.peers(), from)) {
        this.respond();
      }
      return true;
    }
    if (op.op === "snapshot") {
      if (!this.seen.first(op.id)) return true; // already merged
      this.deps.applySnapshot(op.ops);
      return true;
    }
    return false;
  }

  /** Reset on reconnect: forget seen ids and re-request the current state. */
  reset(): void {
    this.seen.reset();
    this.request();
  }

  private respond(): void {
    const op: SnapshotOp = {
      op: "snapshot",
      id: this.deps.newId(),
      author: this.deps.myUserID,
      ts: this.deps.now(),
      ops: this.deps.snapshotOps(),
      ...(this.deps.boardId ? { board_id: this.deps.boardId } : {}),
    };
    this.seen.first(op.id); // don't re-apply our own snapshot if it echoes back
    this.deps.send(op);
  }
}
